import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Play, Square, AlertTriangle, TrendingUp, TrendingDown, 
  Activity, DollarSign, Target, Zap, RefreshCw,
  XCircle, CheckCircle, Loader2, Plus, Trash2, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SYMBOLS = [
  { symbol: 'BTCUSDT', name: 'Bitcoin' },
  { symbol: 'ETHUSDT', name: 'Ethereum' },
  { symbol: 'SOLUSDT', name: 'Solana' },
  { symbol: 'BNBUSDT', name: 'BNB' }
];

const STRATEGIES = [
  { id: 'RFS', name: 'Ruptura Fractal', desc: 'Breakout + Volumen' },
  { id: 'TRS', name: 'Resorte Mecánico', desc: 'Pullback + Stoch' },
  { id: 'ARR', name: 'Asian Range Reversal', desc: 'Reversal sesión Asia' },
  { id: 'CLS', name: 'Claude Strategy', desc: 'Adaptativa multi-régimen' }
];

export default function AutoTrading() {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [balances, setBalances] = useState({ spot: 0, futures: 0 });
  const [selectedBotDebug, setSelectedBotDebug] = useState(null);
  const [debugLog, setDebugLog] = useState([]);
  
  const [config, setConfig] = useState({
    symbol: 'ETHUSDT',
    strategy: 'TRS',
    market_type: 'spot',
    leverage: 1,
    interval: '5m',
    trading_capital: 100,
    base_risk_pct: 0.01,
    tp_ratio: 2.0,
    use_break_even: true,
    lookback: 10,
    vol_multiplier: 1.5,
    use_hmm_filter: false
  });

  const fetchBots = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bots`);
      setBots(response.data.bots || []);
      setBalances({
        spot: response.data.spot_balance || 0,
        futures: response.data.futures_balance || 0
      });
    } catch (error) {
      console.error('Error fetching bots:', error);
    }
  }, []);

  const fetchDebug = useCallback(async (botId) => {
    try {
      const response = await axios.get(`${API_URL}/api/bots/${botId}/debug`);
      setDebugLog(response.data.debug_log || []);
    } catch (error) {
      console.error('Error fetching debug:', error);
    }
  }, []);

  useEffect(() => {
    fetchBots().then(() => setLoading(false));
    const interval = setInterval(fetchBots, 5000);
    return () => clearInterval(interval);
  }, [fetchBots]);

  useEffect(() => {
    if (selectedBotDebug) {
      fetchDebug(selectedBotDebug);
      const interval = setInterval(() => fetchDebug(selectedBotDebug), 5000);
      return () => clearInterval(interval);
    }
  }, [selectedBotDebug, fetchDebug]);

  const startBot = async () => {
    setStarting(true);
    try {
      const response = await axios.post(`${API_URL}/api/bots/start`, config);
      toast.success(`🤖 Bot iniciado: ${response.data.bot_id}`);
      await fetchBots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar bot');
    } finally {
      setStarting(false);
    }
  };

  const stopBot = async (botId) => {
    if (!window.confirm(`¿Detener bot ${botId}?`)) return;
    try {
      const response = await axios.post(`${API_URL}/api/bots/${botId}/stop`);
      toast.success(`Bot detenido. PnL: $${response.data.final_stats?.total_pnl?.toFixed(2) || 0}`);
      if (selectedBotDebug === botId) setSelectedBotDebug(null);
      await fetchBots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al detener bot');
    }
  };

  const stopAllBots = async () => {
    if (!window.confirm('¿Detener TODOS los bots?')) return;
    try {
      await axios.post(`${API_URL}/api/bots/stop-all`);
      toast.success('Todos los bots detenidos');
      setSelectedBotDebug(null);
      await fetchBots();
    } catch (error) {
      toast.error('Error al detener bots');
    }
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Loader2 className="loading-spinner" size={40} />
      </div>
    );
  }

  return (
    <div>
      <header className="page-header">
        <div className="page-header-content">
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>
            <Zap size={24} style={{ display: 'inline', marginRight: '0.5rem', color: '#F59E0B' }} />
            Multi-Bot Trading
          </h1>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <span className="badge" style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)', color: '#10B981' }}>
              {bots.length} bot{bots.length !== 1 ? 's' : ''} activo{bots.length !== 1 ? 's' : ''}
            </span>
            <button onClick={fetchBots} className="btn btn-outline" style={{ padding: '0.25rem 0.5rem' }}>
              <RefreshCw size={16} />
            </button>
          </div>
        </div>
      </header>

      <div className="page-container">
        {/* Warning */}
        <div className="card" style={{ marginBottom: '1rem', backgroundColor: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
          <div style={{ padding: '0.75rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <AlertTriangle size={20} color="#F59E0B" />
            <span style={{ fontSize: '0.875rem', color: '#E6E8EC' }}>
              <strong>⚠️ Dinero Real</strong> - Los bots ejecutan trades reales en Binance
            </span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: '1.5rem' }}>
          {/* Left: New Bot Config */}
          <div>
            {/* Balances */}
            <div className="card" style={{ marginBottom: '1rem' }}>
              <div className="card-content" style={{ padding: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#9AA3AF', fontSize: '0.875rem' }}>Spot:</span>
                  <span style={{ color: '#10B981', fontWeight: 600 }}>${balances.spot.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#9AA3AF', fontSize: '0.875rem' }}>Futures:</span>
                  <span style={{ color: '#F59E0B', fontWeight: 600 }}>${balances.futures.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* New Bot Form */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title"><Plus size={18} /> Nuevo Bot</h3>
              </div>
              <div className="card-content">
                {/* Market Type */}
                <div className="form-group">
                  <label className="form-label">Mercado</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => { updateConfig('market_type', 'spot'); updateConfig('leverage', 1); }}
                      className={`btn ${config.market_type === 'spot' ? 'btn-primary' : 'btn-outline'}`}
                      style={{ flex: 1 }}
                    >
                      Spot
                    </button>
                    <button
                      onClick={() => updateConfig('market_type', 'futures')}
                      className={`btn ${config.market_type === 'futures' ? 'btn-primary' : 'btn-outline'}`}
                      style={{ flex: 1, backgroundColor: config.market_type === 'futures' ? '#F59E0B' : undefined }}
                    >
                      Futures
                    </button>
                  </div>
                </div>

                {config.market_type === 'futures' && (
                  <div className="form-group">
                    <label className="form-label">Leverage</label>
                    <select value={config.leverage} onChange={(e) => updateConfig('leverage', parseInt(e.target.value))} className="form-select">
                      {[1, 2, 3, 5, 10].map(l => <option key={l} value={l}>{l}x</option>)}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Par</label>
                  <select value={config.symbol} onChange={(e) => updateConfig('symbol', e.target.value)} className="form-select">
                    {SYMBOLS.map(s => <option key={s.symbol} value={s.symbol}>{s.name}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Estrategia</label>
                  <select value={config.strategy} onChange={(e) => updateConfig('strategy', e.target.value)} className="form-select">
                    {STRATEGIES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>

                {/* RFS Parameters */}
                {config.strategy === 'RFS' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Consolidation Lookback</label>
                      <input
                        type="number"
                        value={config.lookback}
                        onChange={(e) => updateConfig('lookback', parseInt(e.target.value))}
                        className="form-input"
                        min="5"
                        max="30"
                      />
                      <p style={{ fontSize: '0.65rem', color: '#9AA3AF', margin: '0.25rem 0 0' }}>
                        Velas para detectar consolidación
                      </p>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Volume Multiplier</label>
                      <input
                        type="number"
                        value={config.vol_multiplier}
                        onChange={(e) => updateConfig('vol_multiplier', parseFloat(e.target.value))}
                        className="form-input"
                        min="1"
                        max="3"
                        step="0.1"
                      />
                      <p style={{ fontSize: '0.65rem', color: '#9AA3AF', margin: '0.25rem 0 0' }}>
                        Volumen mínimo vs promedio para ruptura
                      </p>
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label className="form-label">Timeframe</label>
                  <select value={config.interval} onChange={(e) => updateConfig('interval', e.target.value)} className="form-select">
                    <option value="5m">5 minutos</option>
                    <option value="15m">15 minutos</option>
                    <option value="1h">1 hora</option>
                    <option value="4h">4 horas</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Capital (USDT)</label>
                  <input
                    type="number"
                    value={config.trading_capital}
                    onChange={(e) => updateConfig('trading_capital', parseFloat(e.target.value))}
                    className="form-input"
                    min="10"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Riesgo (%)</label>
                  <input
                    type="number"
                    value={config.base_risk_pct * 100}
                    onChange={(e) => updateConfig('base_risk_pct', parseFloat(e.target.value) / 100)}
                    className="form-input"
                    min="0.5"
                    max="5"
                    step="0.5"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">TP Ratio</label>
                  <input
                    type="number"
                    value={config.tp_ratio}
                    onChange={(e) => updateConfig('tp_ratio', parseFloat(e.target.value))}
                    className="form-input"
                    min="1"
                    max="5"
                    step="0.5"
                  />
                  <p style={{ fontSize: '0.65rem', color: '#9AA3AF', margin: '0.25rem 0 0' }}>
                    TP = {config.tp_ratio}x el riesgo
                  </p>
                </div>

                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="use_be"
                    checked={config.use_break_even}
                    onChange={(e) => updateConfig('use_break_even', e.target.checked)}
                    style={{ width: '18px', height: '18px', accentColor: '#14B8A6' }}
                  />
                  <label htmlFor="use_be" style={{ fontSize: '0.875rem', color: '#E6E8EC', cursor: 'pointer' }}>
                    Move to Break-Even at 1R
                  </label>
                </div>
                <p style={{ fontSize: '0.65rem', color: '#9AA3AF', margin: '0.25rem 0 0 1.5rem' }}>
                  Mueve SL a entrada cuando gana 1x el riesgo
                </p>

                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem' }}>
                  <input
                    type="checkbox"
                    id="use_hmm"
                    checked={config.use_hmm_filter}
                    onChange={(e) => updateConfig('use_hmm_filter', e.target.checked)}
                    style={{ width: '18px', height: '18px', accentColor: '#8B5CF6' }}
                  />
                  <label htmlFor="use_hmm" style={{ fontSize: '0.875rem', color: '#E6E8EC', cursor: 'pointer' }}>
                    🧠 Filtro HMM (Markov)
                  </label>
                </div>
                <p style={{ fontSize: '0.65rem', color: '#8B5CF6', margin: '0.25rem 0 0 1.5rem' }}>
                  Filtra señales por régimen de mercado (bull/bear/choppy)
                </p>

                <button
                  onClick={startBot}
                  disabled={starting}
                  className="btn btn-primary"
                  style={{ width: '100%', marginTop: '1rem' }}
                >
                  {starting ? <><Loader2 className="loading-spinner" size={18} /> Iniciando...</> : <><Play size={18} /> Iniciar Bot</>}
                </button>
              </div>
            </div>

            {bots.length > 0 && (
              <button
                onClick={stopAllBots}
                className="btn"
                style={{ width: '100%', marginTop: '1rem', backgroundColor: '#EF4444', color: 'white' }}
              >
                <Square size={16} /> Detener Todos
              </button>
            )}
          </div>

          {/* Right: Active Bots */}
          <div>
            {bots.length === 0 ? (
              <div className="card">
                <div className="card-content" style={{ textAlign: 'center', padding: '3rem', color: '#9AA3AF' }}>
                  <Zap size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                  <p>No hay bots activos</p>
                  <p style={{ fontSize: '0.875rem' }}>Configura y lanza un nuevo bot desde el panel izquierdo</p>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {bots.map(bot => (
                  <div key={bot.bot_id} className="card" style={{ border: bot.open_position ? '1px solid #14B8A6' : undefined }}>
                    <div className="card-content" style={{ padding: '1rem' }}>
                      {/* Header */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Activity size={18} color="#10B981" />
                          <span style={{ fontWeight: 600, color: '#E6E8EC' }}>{bot.symbol}</span>
                          <span className="badge" style={{ backgroundColor: 'rgba(20, 184, 166, 0.2)', color: '#14B8A6' }}>
                            {bot.strategy}
                          </span>
                          <span className="badge" style={{ 
                            backgroundColor: bot.market_type === 'futures' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(107, 114, 128, 0.2)',
                            color: bot.market_type === 'futures' ? '#F59E0B' : '#9AA3AF'
                          }}>
                            {bot.market_type === 'futures' ? `⚡${bot.leverage}x` : 'Spot'}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#9AA3AF' }}>{bot.interval}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => setSelectedBotDebug(selectedBotDebug === bot.bot_id ? null : bot.bot_id)}
                            className="btn btn-outline"
                            style={{ padding: '0.25rem 0.5rem' }}
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            onClick={() => stopBot(bot.bot_id)}
                            className="btn"
                            style={{ padding: '0.25rem 0.5rem', backgroundColor: '#EF4444', color: 'white' }}
                          >
                            <Square size={14} />
                          </button>
                        </div>
                      </div>

                      {/* Stats */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                          <p style={{ fontSize: '0.7rem', color: '#9AA3AF', margin: 0 }}>Equity</p>
                          <p style={{ fontSize: '1rem', fontWeight: 600, color: '#E6E8EC', margin: 0 }}>
                            ${bot.current_equity?.toFixed(2) || 0}
                          </p>
                        </div>
                        <div>
                          <p style={{ fontSize: '0.7rem', color: '#9AA3AF', margin: 0 }}>PnL</p>
                          <p style={{ fontSize: '1rem', fontWeight: 600, color: bot.total_pnl >= 0 ? '#10B981' : '#EF4444', margin: 0 }}>
                            ${bot.total_pnl?.toFixed(2) || 0}
                          </p>
                        </div>
                        <div>
                          <p style={{ fontSize: '0.7rem', color: '#9AA3AF', margin: 0 }}>Trades</p>
                          <p style={{ fontSize: '1rem', fontWeight: 600, color: '#E6E8EC', margin: 0 }}>
                            {bot.total_trades || 0}
                          </p>
                        </div>
                        <div>
                          <p style={{ fontSize: '0.7rem', color: '#9AA3AF', margin: 0 }}>Win Rate</p>
                          <p style={{ fontSize: '1rem', fontWeight: 600, color: '#E6E8EC', margin: 0 }}>
                            {bot.total_trades > 0 ? ((bot.winning_trades / bot.total_trades) * 100).toFixed(0) : 0}%
                          </p>
                        </div>
                      </div>

                      {/* Open Position */}
                      {bot.open_position && (
                        <div style={{ padding: '0.75rem', backgroundColor: '#0B0D0F', borderRadius: '8px', marginBottom: '1rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                            {bot.open_position.type === 'LONG' ? <TrendingUp size={16} color="#10B981" /> : <TrendingDown size={16} color="#EF4444" />}
                            <span style={{ fontWeight: 600, color: bot.open_position.type === 'LONG' ? '#10B981' : '#EF4444' }}>
                              {bot.open_position.type}
                            </span>
                            <span style={{ fontSize: '0.8rem', color: '#E6E8EC' }}>@ ${bot.open_position.entry?.toFixed(2)}</span>
                          </div>
                          <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem' }}>
                            <span style={{ color: '#EF4444' }}>SL: ${bot.open_position.stop_loss?.toFixed(2)}</span>
                            <span style={{ color: '#10B981' }}>TP: ${bot.open_position.take_profit?.toFixed(2)}</span>
                          </div>
                        </div>
                      )}

                      {/* Last Signal */}
                      <div style={{ padding: '0.5rem', backgroundColor: '#0B0D0F', borderRadius: '6px', fontSize: '0.75rem' }}>
                        <span style={{ color: '#9AA3AF' }}>Estado: </span>
                        <span style={{ color: '#E6E8EC', fontFamily: 'monospace' }}>{bot.last_signal || 'Esperando...'}</span>
                        {bot.last_candle_time && (
                          <span style={{ color: '#6B7280', marginLeft: '0.5rem' }}>| {bot.last_candle_time}</span>
                        )}
                      </div>

                      {/* Debug Log */}
                      {selectedBotDebug === bot.bot_id && debugLog.length > 0 && (
                        <div style={{ marginTop: '1rem', maxHeight: '200px', overflow: 'auto' }}>
                          <table className="table" style={{ fontSize: '0.65rem' }}>
                            <thead>
                              <tr>
                                <th>Vela</th>
                                <th>$</th>
                                <th>K</th>
                                <th>Trend</th>
                                <th>PB</th>
                                <th>Prev</th>
                                <th>Cross</th>
                                <th>Señal</th>
                              </tr>
                            </thead>
                            <tbody>
                              {debugLog.slice().reverse().slice(0, 20).map((log, idx) => (
                                <tr key={idx} style={{ backgroundColor: log.signal ? 'rgba(16, 185, 129, 0.1)' : 'transparent' }}>
                                  <td>{log.time}</td>
                                  <td>{log.price}</td>
                                  <td>{log.k}</td>
                                  <td style={{ color: log.trend === 'LONG' ? '#10B981' : '#EF4444' }}>{log.trend}</td>
                                  <td style={{ color: log.pullback ? '#10B981' : '#EF4444' }}>{log.pullback ? '✓' : '✗'}</td>
                                  <td style={{ color: (log.prev_ob || log.prev_os) ? '#10B981' : '#EF4444' }}>
                                    {log.prev_ob ? '>80' : log.prev_os ? '<20' : '✗'}
                                  </td>
                                  <td style={{ color: log.cross !== '-' ? '#10B981' : '#EF4444' }}>{log.cross}</td>
                                  <td style={{ color: log.signal ? '#10B981' : '#6B7280', fontWeight: log.signal ? 700 : 400 }}>
                                    {log.signal || '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
